import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Plus, Upload, Edit2, Copy, Trash2, GraduationCap, CalendarDays, Clock,
} from 'lucide-react';
import { format } from 'date-fns';

export const SessionsTab = ({ sessions, courses, onAddSession, onEditSession, onCopySession, onDeleteSession, onBulkImportOpen, isCompleted, highlightSessionId }) => {
  const rowRefs = useRef({});
  const [highlighted, setHighlighted] = useState(null);

  useEffect(() => {
    if (!highlightSessionId) return;
    setHighlighted(highlightSessionId);
    const el = rowRefs.current[highlightSessionId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlighted(null), 2500);
    return () => clearTimeout(t);
  }, [highlightSessionId, sessions.length]);

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.title || courseId;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Training Sessions</h3>
          <p className="text-sm text-muted-foreground">Each session covers one course</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBulkImportOpen}
            disabled={isCompleted}
            title={isCompleted ? 'Project is completed — sessions can no longer be added' : undefined}
            data-testid="bulk-import-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Sessions
          </Button>
          <Button
            onClick={onAddSession}
            disabled={isCompleted}
            title={isCompleted ? 'Project is completed — sessions can no longer be added' : undefined}
            className="bg-lime text-lime-foreground hover:bg-lime/90"
            data-testid="add-session-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Session
          </Button>
        </div>
      </div>
      {isCompleted && (
        <p className="text-xs text-muted-foreground -mt-4" data-testid="sessions-completed-notice">
          This project is marked Completed — adding, importing or duplicating sessions is disabled.
        </p>
      )}

      {sessions.length > 0 ? (
        <Table data-testid="sessions-detail-table">
          <TableHeader>
            <TableRow>
              <TableHead>SESSION</TableHead>
              <TableHead>DATE</TableHead>
              <TableHead>TIME</TableHead>
              <TableHead>COURSE</TableHead>
              <TableHead>TRAINER</TableHead>
              <TableHead>TYPE</TableHead>
              <TableHead className="w-24">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow
                key={session.id}
                ref={(el) => { rowRefs.current[session.id] = el; }}
                className={highlighted === session.id ? 'bg-lime/20 transition-colors duration-1000' : ''}
                data-testid={`session-row-${session.id}`}
              >
                <TableCell>
                  <div>
                    <p className="font-medium">{session.title}</p>
                    {session.is_multi_day && (
                      <Badge variant="secondary" className="text-xs mt-0.5">Multi-day</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>
                      {session.session_date ? format(new Date(session.session_date), 'MMM d, yyyy') : '—'}
                      {session.is_multi_day && session.end_date && ` – ${format(new Date(session.end_date), 'MMM d')}`}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{session.start_time} – {session.end_time}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <GraduationCap className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{session.course_title || (session.course_id ? getCourseName(session.course_id) : null) || '—'}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {session.trainer_name ? (
                    <div>
                      <p className="text-sm">{session.trainer_name}</p>
                      {session.trainer_2_name && (
                        <p className="text-xs text-muted-foreground">{session.trainer_2_name}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{session.session_type?.replace('_', '-')}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => onEditSession(session)} data-testid={`edit-session-${session.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onCopySession(session)}
                      disabled={isCompleted}
                      data-testid={`copy-session-${session.id}`}
                      title={isCompleted ? 'Project is completed — cannot duplicate sessions' : 'Duplicate session'}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDeleteSession(session.id)} className="text-destructive" data-testid={`delete-session-${session.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No sessions yet</h3>
              <p className="text-muted-foreground mb-4">Add sessions to this training project</p>
              <Button onClick={onAddSession} disabled={isCompleted} title={isCompleted ? 'Project is completed — sessions can no longer be added' : undefined}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Session
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SessionsTab;
